import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  PlusIcon,
  PlayIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/20/solid';

// Complete data sources from schema
const DATA_SOURCES = {
  jobs: {
    label: 'All Jobs',
    table: 'jobs',
    fields: [
      { key: 'id', label: 'Job ID', type: 'number' },
      { key: 'po_number', label: 'PO Number', type: 'text' },
      { key: 'po_line_number', label: 'Line #', type: 'number' },
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'revision', label: 'Revision', type: 'text' },
      { key: 'status', label: 'Status', type: 'select', options: ['not_started', 'in_production', 'ready_for_shipment', 'partially_shipped', 'closed'] },
      { key: 'quantity_on_order', label: 'Qty Ordered', type: 'number' },
      { key: 'produced_quantity', label: 'Qty Produced', type: 'number' },
      { key: 'shipped_quantity', label: 'Qty Shipped', type: 'number' },
      { key: 'unit_price', label: 'Unit Price', type: 'number' },
      { key: 'total_price', label: 'Total Price', type: 'number' },
      { key: 'labor_cost', label: 'Labor Cost', type: 'number' },
      { key: 'material_cost', label: 'Material Cost', type: 'number' },
      { key: 'overhead_cost', label: 'Overhead Cost', type: 'number' },
      { key: 'expected_delivery_date', label: 'Due Date', type: 'date' },
      { key: 'po_start_date', label: 'PO Start Date', type: 'date' },
      { key: 'date_completed', label: 'Date Completed', type: 'date' },
      { key: 'date_closed', label: 'Date Closed', type: 'date' },
      { key: 'high_priority', label: 'High Priority', type: 'boolean' },
      { key: 'notes', label: 'Notes', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'shops.name', label: 'Shop', type: 'text', join: 'shops' },
      { key: 'customers.name', label: 'Customer', type: 'text', join: 'customers' },
      { key: 'customers.nickname', label: 'Customer Nickname', type: 'text', join: 'customers' },
      { key: 'machines.name', label: 'Machine', type: 'text', join: 'machines!jobs_machine_id_fkey' },
    ],
  },
  parts: {
    label: 'Parts',
    table: 'parts',
    fields: [
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'part_number_master', label: 'Part # Master', type: 'text' },
      { key: 'revision', label: 'Revision', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'material', label: 'Material', type: 'text' },
      { key: 'inventory_quantity', label: 'Inventory Qty', type: 'number' },
      { key: 'minimum_stock_level', label: 'Min Stock Level', type: 'number' },
      { key: 'kanban_stock', label: 'Kanban Stock', type: 'number' },
      { key: 'batch_size', label: 'Batch Size', type: 'number' },
      { key: 'unit_price', label: 'Unit Price', type: 'number' },
      { key: 'average_run_time', label: 'Avg Run Time', type: 'number' },
      { key: 'stock_usage', label: 'Stock Usage', type: 'number' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'setup_notes', label: 'Setup Notes', type: 'text' },
      { key: 'production_notes', label: 'Production Notes', type: 'text' },
      { key: 'last_run_date', label: 'Last Run Date', type: 'datetime' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'shops.name', label: 'Shop', type: 'text', join: 'shops' },
      { key: 'machines.name', label: 'Machine', type: 'text', join: 'machines' },
      { key: 'suppliers.name', label: 'Supplier', type: 'text', join: 'suppliers' },
    ],
  },
  production_logs: {
    label: 'Production Logs',
    table: 'production_logs',
    fields: [
      { key: 'log_id', label: 'Log ID', type: 'number' },
      { key: 'production_date', label: 'Date', type: 'date' },
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'quantity_produced', label: 'Qty Produced', type: 'number' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'jobs.po_number', label: 'PO Number', type: 'text', join: 'jobs' },
      { key: 'jobs.description', label: 'Job Description', type: 'text', join: 'jobs' },
      { key: 'shops.name', label: 'Shop', type: 'text', join: 'shops' },
      { key: 'machines.name', label: 'Machine', type: 'text', join: 'machines' },
    ],
  },
  deliveries: {
    label: 'Deliveries',
    table: 'deliveries',
    fields: [
      { key: 'delivery_id', label: 'Delivery ID', type: 'number' },
      { key: 'package_number', label: 'Package #', type: 'text' },
      { key: 'delivery_number', label: 'Delivery #', type: 'number' },
      { key: 'po_number', label: 'PO Number', type: 'text' },
      { key: 'po_line_number', label: 'Line #', type: 'number' },
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'quantity', label: 'Quantity', type: 'number' },
      { key: 'ship_date', label: 'Ship Date', type: 'date' },
      { key: 'ship_by', label: 'Ship By', type: 'text' },
      { key: 'tracking_number', label: 'Tracking #', type: 'text' },
      { key: 'invoice_number', label: 'Invoice #', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'jobs.description', label: 'Job Description', type: 'text', join: 'jobs' },
      { key: 'jobs.unit_price', label: 'Unit Price', type: 'number', join: 'jobs' },
    ],
  },
  customers: {
    label: 'Customers',
    table: 'customers',
    fields: [
      { key: 'id', label: 'Customer ID', type: 'number' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'nickname', label: 'Nickname', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'billing_address', label: 'Billing Address', type: 'text' },
      { key: 'billing_city', label: 'Billing City', type: 'text' },
      { key: 'billing_state', label: 'Billing State', type: 'text' },
      { key: 'billing_zip', label: 'Billing Zip', type: 'text' },
      { key: 'billing_contact_name', label: 'Billing Contact', type: 'text' },
      { key: 'billing_contact_email', label: 'Billing Email', type: 'text' },
      { key: 'shipping_address', label: 'Shipping Address', type: 'text' },
      { key: 'shipping_city', label: 'Shipping City', type: 'text' },
      { key: 'shipping_state', label: 'Shipping State', type: 'text' },
      { key: 'shipping_zip', label: 'Shipping Zip', type: 'text' },
      { key: 'shipping_contact_name', label: 'Shipping Contact', type: 'text' },
      { key: 'payment_terms', label: 'Payment Terms', type: 'text' },
      { key: 'net_terms', label: 'Net Terms (Days)', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
    ],
  },
  kanban_jobs: {
    label: 'Kanban Jobs',
    table: 'kanban_jobs',
    fields: [
      { key: 'id', label: 'Kanban Job ID', type: 'number' },
      { key: 'po_number', label: 'PO Number', type: 'text' },
      { key: 'po_line_number', label: 'Line #', type: 'number' },
      { key: 'part_number', label: 'Part Number', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'quantity_on_order', label: 'Qty Ordered', type: 'number' },
      { key: 'produced_quantity', label: 'Qty Produced', type: 'number' },
      { key: 'shipped_quantity', label: 'Qty Shipped', type: 'number' },
      { key: 'unit_price', label: 'Unit Price', type: 'number' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'po_start_date', label: 'PO Start Date', type: 'date' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'shops.name', label: 'Shop', type: 'text', join: 'shops' },
      { key: 'customers.name', label: 'Customer', type: 'text', join: 'customers' },
      { key: 'customers.nickname', label: 'Customer Nickname', type: 'text', join: 'customers' },
    ],
  },
  invoices: {
    label: 'Invoices',
    table: 'invoices',
    fields: [
      { key: 'id', label: 'Invoice ID', type: 'number' },
      { key: 'invoice_number', label: 'Invoice #', type: 'text' },
      { key: 'package_number', label: 'Package #', type: 'text' },
      { key: 'total_quantity', label: 'Total Qty', type: 'number' },
      { key: 'total_amount', label: 'Total Amount', type: 'number' },
      { key: 'status', label: 'Status', type: 'select', options: ['unpaid', 'paid', 'overdue', 'cancelled'] },
      { key: 'reference_number', label: 'Reference #', type: 'text' },
      { key: 'payment_date', label: 'Payment Date', type: 'date' },
      { key: 'net_due_date', label: 'Due Date', type: 'date' },
      { key: 'payment_type', label: 'Payment Type', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'customers.name', label: 'Customer', type: 'text', join: 'customers' },
      { key: 'customers.nickname', label: 'Customer Nickname', type: 'text', join: 'customers' },
    ],
  },
  machines: {
    label: 'Machines',
    table: 'machines',
    fields: [
      { key: 'id', label: 'Machine ID', type: 'number' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'capacity_per_day', label: 'Capacity/Day', type: 'number' },
      { key: 'current_status', label: 'Current Status', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'shops.name', label: 'Shop', type: 'text', join: 'shops' },
    ],
  },
  shops: {
    label: 'Shops',
    table: 'shops',
    fields: [
      { key: 'id', label: 'Shop ID', type: 'number' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
    ],
  },
  suppliers: {
    label: 'Suppliers',
    table: 'suppliers',
    fields: [
      { key: 'id', label: 'Supplier ID', type: 'number' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'contact_name', label: 'Contact Name', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'address', label: 'Address', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'postal_code', label: 'Postal Code', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
    ],
  },
  materials: {
    label: 'Materials',
    table: 'materials',
    fields: [
      { key: 'id', label: 'Material ID', type: 'number' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'cost', label: 'Cost', type: 'number' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
    ],
  },
  general_quotes: {
    label: 'Quotes',
    table: 'general_quotes',
    fields: [
      { key: 'id', label: 'Quote ID', type: 'number' },
      { key: 'quote_name', label: 'Quote Name', type: 'text' },
      { key: 'quote_type', label: 'Quote Type', type: 'text' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'custom_customer_name', label: 'Customer Name', type: 'text' },
      { key: 'custom_customer_email', label: 'Customer Email', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'text' },
      { key: 'created_at', label: 'Created At', type: 'datetime' },
      { key: 'customers.name', label: 'Customer (Linked)', type: 'text', join: 'customers' },
    ],
  },
};

const OPERATORS = {
  text: [
    { value: 'eq', label: 'equals' },
    { value: 'neq', label: 'not equals' },
    { value: 'ilike', label: 'contains' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  number: [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  date: [
    { value: 'eq', label: 'equals' },
    { value: 'gt', label: 'after' },
    { value: 'lt', label: 'before' },
    { value: 'gte', label: 'on or after' },
    { value: 'lte', label: 'on or before' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  datetime: [
    { value: 'gt', label: 'after' },
    { value: 'lt', label: 'before' },
    { value: 'gte', label: 'on or after' },
    { value: 'lte', label: 'on or before' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  select: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  boolean: [
    { value: 'eq', label: 'is' },
  ],
};

export default function Reports() {
  const { user } = useAuth();
  const [dataSource, setDataSource] = useState('jobs');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [resultLimit, setResultLimit] = useState(500);

  // Initialize columns when data source changes
  useEffect(() => {
    const source = DATA_SOURCES[dataSource];
    setSelectedColumns(source.fields.slice(0, 6).map(f => f.key));
    setFilters([]);
    setSortField(source.fields[0]?.key || '');
    setResults([]);
    setHasRun(false);
  }, [dataSource]);

  useEffect(() => {
    document.title = 'Reports';
  }, []);

  const toggleColumn = (columnKey) => {
    setSelectedColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(c => c !== columnKey)
        : [...prev, columnKey]
    );
  };

  const selectAllColumns = () => {
    const source = DATA_SOURCES[dataSource];
    setSelectedColumns(source.fields.map(f => f.key));
  };

  const clearAllColumns = () => {
    setSelectedColumns([]);
  };

  const addFilter = () => {
    const source = DATA_SOURCES[dataSource];
    const firstField = source.fields.find(f => !f.join) || source.fields[0];
    const fieldType = firstField.type || 'text';
    setFilters(prev => [...prev, {
      id: Date.now(),
      field: firstField.key,
      operator: OPERATORS[fieldType]?.[0]?.value || 'eq',
      value: '',
    }]);
  };

  const updateFilter = (id, updates) => {
    setFilters(prev => prev.map(f => {
      if (f.id !== id) return f;
      if (updates.field && updates.field !== f.field) {
        const field = DATA_SOURCES[dataSource].fields.find(fld => fld.key === updates.field);
        const fieldType = field?.type || 'text';
        const operators = OPERATORS[fieldType] || OPERATORS.text;
        return { ...f, ...updates, operator: operators[0].value, value: '' };
      }
      return { ...f, ...updates };
    }));
  };

  const removeFilter = (id) => {
    setFilters(prev => prev.filter(f => f.id !== id));
  };

  const getFieldType = (fieldKey) => {
    const field = DATA_SOURCES[dataSource].fields.find(f => f.key === fieldKey);
    return field?.type || 'text';
  };

  const getFieldOptions = (fieldKey) => {
    const field = DATA_SOURCES[dataSource].fields.find(f => f.key === fieldKey);
    return field?.options || [];
  };

  const runReport = async () => {
    if (!user) return;
    if (selectedColumns.length === 0) {
      toast.error('Please select at least one column');
      return;
    }

    setLoading(true);
    setHasRun(true);

    try {
      const source = DATA_SOURCES[dataSource];

      // Build select string with joins
      const joins = new Set();
      const selectParts = selectedColumns.map(col => {
        const field = source.fields.find(f => f.key === col);
        if (field?.join) {
          joins.add(field.join);
        }
        return col;
      });

      // Add joins for filters too
      filters.forEach(filter => {
        const field = source.fields.find(f => f.key === filter.field);
        if (field?.join) {
          joins.add(field.join);
        }
      });

      // Build select string
      let selectString = selectParts.filter(p => !p.includes('.')).join(', ');
      joins.forEach(join => {
        // Extract base table name (e.g., 'machines' from 'machines!jobs_machine_id_fkey')
        const baseTable = join.includes('!') ? join.split('!')[0] : join;
        const joinFields = selectParts.filter(p => p.startsWith(baseTable + '.'));
        if (joinFields.length > 0) {
          selectString += `, ${join}(${joinFields.map(f => f.split('.')[1]).join(', ')})`;
        } else {
          selectString += `, ${join}(*)`;
        }
      });

      let query = supabase.from(source.table).select(selectString);

      // Apply filters (only for non-joined fields)
      for (const filter of filters) {
        const field = source.fields.find(f => f.key === filter.field);
        if (field?.join) continue; // Skip joined field filters for now

        if (filter.operator === 'is_null') {
          query = query.is(filter.field, null);
        } else if (filter.operator === 'not_null') {
          query = query.not(filter.field, 'is', null);
        } else if (filter.operator === 'ilike') {
          query = query.ilike(filter.field, `%${filter.value}%`);
        } else if (filter.value !== '') {
          query = query[filter.operator](filter.field, filter.value);
        }
      }

      // Apply sorting (only for non-joined fields)
      if (sortField && !sortField.includes('.')) {
        query = query.order(sortField, { ascending: sortDirection === 'asc' });
      }

      query = query.limit(resultLimit);

      const { data, error } = await query;

      if (error) throw error;

      // Flatten joined data
      const flattenedData = (data || []).map(row => {
        const flat = {};
        selectedColumns.forEach(col => {
          if (col.includes('.')) {
            const [table, field] = col.split('.');
            flat[col] = row[table]?.[field] ?? '';
          } else {
            flat[col] = row[col] ?? '';
          }
        });
        return flat;
      });

      setResults(flattenedData);
      toast.success(`Found ${flattenedData.length} results${flattenedData.length >= resultLimit ? ` (limited to ${resultLimit})` : ''}`);
    } catch (error) {
      console.error('Error running report:', error);
      toast.error(`Failed to run report: ${error.message}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (results.length === 0) {
      toast.error('No data to export');
      return;
    }

    const source = DATA_SOURCES[dataSource];
    const headers = selectedColumns.map(col => {
      const field = source.fields.find(f => f.key === col);
      return field?.label || col;
    });

    const csvContent = [
      headers.join(','),
      ...results.map(row =>
        selectedColumns.map(col => {
          let value = row[col];
          if (value === null || value === undefined) value = '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${dataSource}_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported to CSV');
  };

  const source = DATA_SOURCES[dataSource];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-slate-800">Reports</h1>
            <select
              value={resultLimit}
              onChange={(e) => setResultLimit(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value={100}>Limit: 100</option>
              <option value={500}>Limit: 500</option>
              <option value={1000}>Limit: 1,000</option>
              <option value={5000}>Limit: 5,000</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runReport}
              disabled={loading || selectedColumns.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  Run Report
                </>
              )}
            </button>
            <button
              onClick={exportCSV}
              disabled={results.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Left Panel - Configuration */}
          <div className="col-span-3 space-y-4">
            {/* Data Source */}
            <div className="bg-white rounded-sm border border-slate-200 p-3">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Data Source</h3>
              <select
                value={dataSource}
                onChange={(e) => setDataSource(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              >
                {Object.entries(DATA_SOURCES).map(([key, src]) => (
                  <option key={key} value={key}>{src.label}</option>
                ))}
              </select>
            </div>

            {/* Columns */}
            <div className="bg-white rounded-sm border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Columns ({selectedColumns.length})</h3>
                <div className="flex gap-1">
                  <button onClick={selectAllColumns} className="text-xs text-slate-500 hover:text-slate-700">All</button>
                  <span className="text-slate-300">|</span>
                  <button onClick={clearAllColumns} className="text-xs text-slate-500 hover:text-slate-700">None</button>
                </div>
              </div>
              <div className="space-y-0.5 max-h-72 overflow-y-auto">
                {source.fields.map(field => (
                  <label key={field.key} className="flex items-center gap-2 cursor-pointer py-0.5 hover:bg-slate-50 px-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(field.key)}
                      onChange={() => toggleColumn(field.key)}
                      className="w-3.5 h-3.5 rounded-sm border-slate-300 text-slate-700 focus:ring-slate-400"
                    />
                    <span className={`text-sm ${field.join ? 'text-slate-500 italic' : 'text-slate-700'}`}>
                      {field.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="bg-white rounded-sm border border-slate-200 p-3">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Sort By</h3>
              <div className="flex gap-2">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                >
                  <option value="">None</option>
                  {source.fields.filter(f => !f.join).map(field => (
                    <option key={field.key} value={field.key}>{field.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="px-2 py-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"
                  title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortDirection === 'asc' ? (
                    <ChevronUpIcon className="w-4 h-4 text-slate-600" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-slate-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Filters & Results */}
          <div className="col-span-9 space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Filters</h3>
                <button
                  onClick={addFilter}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-sm transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Filter
                </button>
              </div>
              {filters.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No filters applied.</p>
              ) : (
                <div className="space-y-2">
                  {filters.map((filter, index) => {
                    const fieldType = getFieldType(filter.field);
                    const operators = OPERATORS[fieldType] || OPERATORS.text;
                    const needsValue = !['is_null', 'not_null'].includes(filter.operator);
                    const field = source.fields.find(f => f.key === filter.field);

                    return (
                      <div key={filter.id} className="flex items-center gap-2 flex-wrap">
                        {index > 0 && (
                          <span className="text-xs text-slate-500 w-8">AND</span>
                        )}
                        <select
                          value={filter.field}
                          onChange={(e) => updateFilter(filter.id, { field: e.target.value })}
                          className="w-44 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          {source.fields.filter(f => !f.join).map(f => (
                            <option key={f.key} value={f.key}>{f.label}</option>
                          ))}
                        </select>
                        <select
                          value={filter.operator}
                          onChange={(e) => updateFilter(filter.id, { operator: e.target.value })}
                          className="w-28 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                          {operators.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                        {needsValue && (
                          fieldType === 'select' ? (
                            <select
                              value={filter.value}
                              onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                              className="flex-1 min-w-32 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            >
                              <option value="">Select...</option>
                              {getFieldOptions(filter.field).map(opt => (
                                <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          ) : fieldType === 'boolean' ? (
                            <select
                              value={filter.value}
                              onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                              className="flex-1 min-w-32 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            >
                              <option value="">Select...</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : fieldType === 'date' || fieldType === 'datetime' ? (
                            <input
                              type="date"
                              value={filter.value}
                              onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                              className="flex-1 min-w-32 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            />
                          ) : (
                            <input
                              type={fieldType === 'number' ? 'number' : 'text'}
                              value={filter.value}
                              onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                              placeholder="Value..."
                              className="flex-1 min-w-32 px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            />
                          )
                        )}
                        <button
                          onClick={() => removeFilter(filter.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-sm transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Results */}
            <div className="bg-white rounded-sm border border-slate-200">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Results</h3>
                {hasRun && (
                  <span className="text-sm text-slate-500">{results.length} row{results.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              {!hasRun ? (
                <div className="p-8 text-center text-slate-400">
                  <p>Select columns and click "Run Report" to see results.</p>
                </div>
              ) : loading ? (
                <div className="p-8 text-center text-slate-400">
                  <svg className="animate-spin h-6 w-6 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p>Running report...</p>
                </div>
              ) : results.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <p>No results found. Try adjusting your filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {selectedColumns.map(col => {
                          const field = source.fields.find(f => f.key === col);
                          return (
                            <th
                              key={col}
                              className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap border-b border-slate-200"
                            >
                              {field?.label || col}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {results.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          {selectedColumns.map(col => (
                            <td key={col} className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap">
                              {formatValue(row[col], getFieldType(col))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === '') return '-';
  if (type === 'date' && value) {
    return new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' });
  }
  if (type === 'datetime' && value) {
    return new Date(value).toLocaleString('en-US', { timeZone: 'UTC' });
  }
  if (type === 'boolean') {
    return value === true || value === 'true' ? 'Yes' : 'No';
  }
  if (type === 'select' && typeof value === 'string') {
    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  if (type === 'number' && typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
}
